import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  TablePagination,
  Box,
  Grid,
  Typography,
  Chip
} from "@mui/material";
import { motion } from "framer-motion";
import configServ from "../../services/config";
import debounce from "lodash.debounce";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";

const AadharLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  // 🔎 Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      fetchLogs(value, page, rowsPerPage);
    }, 500),
    []
  );

  // fetch logs API
  const fetchLogs = async (searchText = "", pageNo = 0, limit = 10) => {
    try {
      setLoading(true);

      const query = {
        page: pageNo + 1, // backend 1-based pagination
        per_page: limit,
        aadhaar_no: searchText || undefined,
      };

      const res = await configServ.getLogs(query);

      if (res.success) {
        setLogs(res.data);
        setCount(res.total);
      } else {
        setLogs([]);
        setCount(0);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  // run on page change or rowsPerPage change
  useEffect(() => {
    fetchLogs(search, page, rowsPerPage);
  }, [page, rowsPerPage]);

  // handle search input
  // handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;

    // agar blank hai to empty string bhej do, warna number cast
    const numericValue = value ? Number(value) : "";

    setSearch(value);
    debouncedSearch(numericValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <motion.div
      style={{ width: "100%" }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Aadhar Log"}
        // handlePopup={handlePopup}
        // isEdit={isEdit}
        // create={isCreate}
        // showCreate={false}
      />
      <Box width="100%" sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {/* Count */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
              Logs Count: {logs.length}
            </Typography>
          </Grid>

          {/* Aadhaar Search */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Search Aadhaar No"
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearchChange}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          width: "100%", // full width
          boxShadow: 3, // MUI shadow instead of Tailwind
          borderRadius: 2, // rounded corners
          mt:3
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
              color: "text.secondary",
            }}
          >
            🚫 No Data Found
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Aadhaar No
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Action
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Status
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Message
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  IP Address
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Created At
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <TableCell>{log.aadhaar_no}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>
                    {log.status === "success" ? (
                      <Chip
                        label="Success"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Failed" color="error" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{log.message}</TableCell>
                  <TableCell>{log.ip_address}</TableCell>
                  <TableCell>
                    {new Date(log.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </motion.div>
  );
};

export default AadharLog;
